import {PanelType} from 'components/FluentUICompat';
import {EntityPage} from 'components';
import {LoadWrapper} from 'components/LoadWrapper';
import {useGetConvolutesQuery} from 'features/AdminSection/hooks/convolutes.generated';
import {useGetGdprRuleQuery} from 'features/AdminSection/hooks/gdprRules.generated';
import React, {useState} from 'react';
import {Control} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {ConvoluteType, VehicleProject, UserRole} from 'types';
import VehicleProjectFormElements from '../components/VehicleProjectFormElements';
import {
	GetVehicleProjectOverviewPageDataDocument,
	useCreateVehicleProjectMutation,
	useDeleteVehicleProjectMutation,
	useUpdateVehicleProjectMutation,
	useGetVehicleProjectOverviewPageDataQuery,
} from '../hooks/vehicleProjects.generated';
import {ApolloCache} from '@apollo/client';
import {VehicleProjectsTooltipTranslationProvider} from '../components/VehicleProjectsTooltipTranslationProvider';
import {HeaderForPagesWithEntityPage} from 'components/HeaderForPagesWithEntityPage';
import {AggregateVehicleProjectsDialog} from '../components/AggregateButtons';
import {ErrorMessage} from 'components/hookForms/ErrorMessage';
import {
	buildCombinedVehicleProjects,
	CombinedVehicleProjectItem,
	mapVehicleProjectToInput,
} from './VehicleProjectCombined';
import {
	useVehicleProjectListColumns,
	vehicleProjectRowRenderer,
} from '../hooks/useVehicleProjectListColumns';
import {SRM_ANTRAG_LINK} from '../VehicleProjectsConstants';

const VehicleProjectsPage: React.FC = () => {
	const {t} = useTranslation('features/vehicleprojects');

	const [createVehicleProjectMutation] = useCreateVehicleProjectMutation();
	const [updateVehicleProjectMutation] = useUpdateVehicleProjectMutation();
	const [deleteVehicleProjectMutation] = useDeleteVehicleProjectMutation();

	const {loading, data} = useGetVehicleProjectOverviewPageDataQuery();

	const vpMasters =
		data?.vehicleProjectMasterWithProjectDetails.map(project => ({
			...project.vehicleProjectMaster,
		})) ?? [];

	const {data: convolutes} = useGetConvolutesQuery();
	const [duplicateError, setDuplicateError] = useState(false);
	const [duplicateMessage, setDuplicateMessage] = useState<string>('');

	const convolute = React.useMemo(
		() =>
			convolutes?.convolutes?.find(
				c => c.convoluteType === ConvoluteType.VehicleProject,
			),
		[convolutes],
	);

	const {data: gdprRule} = useGetGdprRuleQuery({
		variables: {
			id: convolute?.gdprRule?.id ?? '',
		},
	});

	const rule = React.useMemo(() => gdprRule?.gdprRule, [gdprRule]);

	const vehicleProjectMasters = React.useMemo(
		() =>
			(vpMasters ?? []).slice().sort((a, b) => {
				const compA = a.modelSeries + a.generation;
				const compB = b.modelSeries + b.generation;
				return compA.localeCompare(compB);
			}),
		[vpMasters],
	);

	const isDuplicateVehicleProject = React.useCallback(
		(
			modelSeries: string | undefined,
			generation: string | undefined,
		): boolean => {
			const ms = (modelSeries ?? '').trim().toLowerCase();
			const gen = (generation ?? '').trim().toLowerCase();
			if (!ms || !gen) return false;

			return vehicleProjectMasters.some(
				vp =>
					(vp?.modelSeries ?? '').trim().toLowerCase() === ms &&
					(vp?.generation ?? '').trim().toLowerCase() === gen,
			);
		},
		[vehicleProjectMasters],
	);
	const refetchQueries = [GetVehicleProjectOverviewPageDataDocument];

	type CacheKeys = string[];

	const getCacheKeysToEvict = (): CacheKeys => {
		return [
			'vehicleProjectsByModelSeriesGenerationActiveTypeMarket',
			'activeTypeMarketsForModelSeriesAndGeneration',
		];
	};

	type ApolloCacheWithTypes = ApolloCache<any>;

	const getCacheEvictorForKey = (cache: ApolloCacheWithTypes) => {
		return (key: CacheKeys[number]): void => {
			cache.evict({fieldName: key});
		};
	};

	const evictCacheForProject = (cache: ApolloCacheWithTypes): void => {
		const cacheKeys: CacheKeys = getCacheKeysToEvict();
		const evictCacheForKey = getCacheEvictorForKey(cache);
		cacheKeys.forEach(evictCacheForKey);
		cache.gc();
	};

	const createVehicleProject = React.useCallback(
		(vehicleProject: VehicleProject) => {
			setDuplicateError(false);
			setDuplicateMessage('');
			if (
				isDuplicateVehicleProject(
					vehicleProject.modelSeries,
					vehicleProject.generation,
				)
			) {
				setDuplicateMessage(
					`A vehicle project for ${vehicleProject.modelSeries} / ${vehicleProject.generation} already exists.`,
				);
				setDuplicateError(true);
				throw new Error('DUPLICATE_VEHICLE_PROJECT');
			}
			vehicleProject.willBeAnonymized = rule?.anonymize ?? false;
			vehicleProject.deletionPeriod = rule?.deletionPeriod ?? 0;
			const input = mapVehicleProjectToInput(vehicleProject);
			createVehicleProjectMutation({
				variables: {
					input: {
						...input,
						isManuallyCreated: true,
					},
				},
				refetchQueries,
				update: evictCacheForProject,
			});
		},
		[isDuplicateVehicleProject],
	);

	const updateVehicleProject = React.useCallback(
		(vehicleProject: VehicleProject) => {
			const input = mapVehicleProjectToInput(vehicleProject);
			updateVehicleProjectMutation({
				variables: {
					input,
				},
				refetchQueries,
				update: evictCacheForProject,
			});
		},
		[],
	);

	const disableVehicleProjectEditing = React.useCallback(
		(selectedItem: VehicleProject): boolean => {
			return !selectedItem.isManuallyCreated;
		},
		[],
	);

	const getEditTooltip = React.useCallback(
		(selectedItem: VehicleProject): string => {
			if (!selectedItem) return '';
			if (!selectedItem.isManuallyCreated) {
				return t('DisableEditVehicleProject');
			}
			return '';
		},
		[],
	);

	const deleteVehicleProject = React.useCallback(
		(masterId: string) => {
			const item = combinedVehicleProjects.find(i => i.id === masterId);
			if (!item) return;

			deleteVehicleProjectMutation({
				variables: {
					input: {id: masterId},
				},
				refetchQueries,
			});
		},
		[refetchQueries],
	);

	const columns = useVehicleProjectListColumns();
	const combinedVehicleProjects = React.useMemo(
		() =>
			buildCombinedVehicleProjects(
				data?.vehicleProjectMasterWithProjectDetails,
			),
		[data?.vehicleProjectMasterWithProjectDetails],
	);
	const formElements = (control: Control<CombinedVehicleProjectItem>) => {
		return (
			<div>
				{duplicateError && (
					<ErrorMessage
						error={{
							message: duplicateMessage,
							type: 'DuplicateVehicleProject',
						}}
					/>
				)}
				<VehicleProjectFormElements control={control} />
			</div>
		);
	};

	const navigate = useNavigate();
	const onViewVehicleProject = React.useCallback(
		(vp: VehicleProject) =>
			navigate(
				`/vehicleProjects/${encodeURIComponent(
					vp.modelSeries,
				)}/${encodeURIComponent(vp.generation)}`,
			),
		[navigate],
	);

	return (
		<>
			<HeaderForPagesWithEntityPage>
				<h3>{t('VehicleProjects')}</h3>
				<p style={{marginTop: 20}}>
					<Trans
						t={t}
						i18nKey='VehicleProjectsAccessPermission'
						components={{
							SRM: (
								<a href={SRM_ANTRAG_LINK} target='_blank' rel='noreferrer' />
							),
						}}
					/>
				</p>
			</HeaderForPagesWithEntityPage>
			<LoadWrapper loading={loading}>
				<VehicleProjectsTooltipTranslationProvider>
					<EntityPage
						items={combinedVehicleProjects}
						entityDisplayName={t('VehicleProjects')}
						createEntity={createVehicleProject}
						deleteEntity={deleteVehicleProject}
						updateEntity={updateVehicleProject}
						panelType={PanelType.smallFluid}
						additionalColumns={columns}
						renderAdditionalFormElements={control => formElements(control)}
						onViewItem={onViewVehicleProject}
						groupListByFieldName={'modelSeries'}
						withNameColumn={false}
						rolesEdit={[
							UserRole.SystemAdministrator,
							UserRole.Bedarfskoordinator,
						]}
						rolesDelete={[UserRole.SystemAdministrator]}
						rolesCreate={[UserRole.SystemAdministrator]}
						disableDelete={disableVehicleProjectEditing}
						disableEdit={disableVehicleProjectEditing}
						editCommandToolTip={getEditTooltip}
						shouldWrapListWithScrollablePane={false}
						entityListProps={{onRenderRow: vehicleProjectRowRenderer}}
					/>
					<AggregateVehicleProjectsDialog />
				</VehicleProjectsTooltipTranslationProvider>
			</LoadWrapper>
		</>
	);
};

export default VehicleProjectsPage;
